'use client';

import { useCallback, useEffect, useState } from 'react';

import getCategoryLabels from '@/app/actions/getCategoryLabels';

/**
 * One in-flight (then settled) request for the whole app.
 *
 * Module-level rather than per-hook: the labels are the same for every caller
 * and change only when the CMS does, so a second search — or a second component
 * asking — should reuse the first answer instead of paying for it again. A
 * rejection resets it so a later open can retry.
 */
let labelsPromise: Promise<Record<string, string>> | null = null;

const loadLabels = (): Promise<Record<string, string>> => {
  labelsPromise ??= getCategoryLabels().catch(error => {
    labelsPromise = null;
    throw error;
  });
  return labelsPromise;
};

/**
 * Resolves a category slug to its CMS title, for the subtitle under a search
 * result. Returns `null` for a slug the hub doesn't have — callers omit the
 * label rather than fall back to a guess at what it should read.
 *
 * `enabled` defers the fetch until something actually needs a label, so merely
 * having a search field in the header costs nothing.
 */
const useCategoryLabel = (enabled: boolean) => {
  const [labels, setLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!enabled) return undefined;

    let stale = false;
    loadLabels()
      .then(next => {
        if (!stale) setLabels(next);
      })
      .catch(() => {
        // A search without category subtitles still works.
      });

    return () => {
      stale = true;
    };
  }, [enabled]);

  return useCallback((slug: string | null): string | null => (slug && labels[slug]) || null, [labels]);
};

export default useCategoryLabel;
